// frontend/src/services/relationshipService.js
import axios from '../axiosConfig';
import i18n from '../i18n';

/**
 * Service for managing user relationships
 * Provides API calls for creating, reading, updating, and deleting user relationships
 */
class RelationshipService {
    
    /**
     * Get translated text
     * @param {string} key - Translation key
     * @param {Object} options - Translation options
     * @returns {string} Translated text
     */
    t(key, options = {}) {
        return i18n.t(key, options);
    }
    /**
     * Get relationships for a user within a specific entity
     * @param {number} entityId - Entity ID for multi-tenant isolation
     * @param {number} userId - User ID
     * @param {string} relationType - Optional filter by relation type
     * @param {string} status - Optional filter by status (default: null = all statuses)
     * @returns {Promise<Object>} API response with relationships array
     */
    async getUserRelationships(entityId, userId) {
        try {
            const response = await axios.get(`/api/entity/${entityId}/users/${userId}/relationships`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user relationships:', error);
            throw this.handleError(error, this.t('errorFetchingRelationships'));
        }
    }

    /**
     * Create a new relationship within a specific entity
     * @param {number} entityId - Entity ID for multi-tenant isolation
     * @param {number} userId - Primary user ID
     * @param {Object} relationshipData - Relationship details
     * @param {number} relationshipData.relatedUserId - Related user ID
     * @param {string} relationshipData.relationType - Type of relationship
     * @param {boolean} relationshipData.isPrimary - Whether this is primary relationship
     * @param {boolean} relationshipData.notificationEnabled - Whether notifications are enabled
     * @returns {Promise<Object>} API response with created relationship
     */
    async createRelationship(entityId, userId, relationshipData) {
        try {
            const response = await axios.post(`/api/entity/${entityId}/users/${userId}/relationships`, relationshipData);
            return response.data;
        } catch (error) {
            console.error('Error creating relationship:', error);
            throw this.handleError(error, this.t('errorCreatingRelationship'));
        }
    }

    /**
     * Update an existing relationship
     * @param {number} entityId - Entity ID for multi-tenant isolation
     * @param {number} userId - User ID
     * @param {number} relationshipId - Relationship ID
     * @param {Object} updateData - Data to update
     * @param {string} updateData.relationType - Type of relationship
     * @param {boolean} updateData.isPrimary - Whether this is primary relationship
     * @param {boolean} updateData.notificationEnabled - Whether notifications are enabled
     * @returns {Promise<Object>} API response with updated relationship
     */
    async updateRelationship(entityId, userId, relationshipId, updateData) {
        try {
            const response = await axios.put(`/api/entity/${entityId}/users/${userId}/relationships/${relationshipId}`, updateData);
            return response.data;
        } catch (error) {
            console.error('Error updating relationship:', error);
            throw this.handleError(error, this.t('errorUpdatingRelationship'));
        }
    }

    /**
     * Update relationship status
     * Note: Entity access is validated server-side based on the relationship's entity and user's permissions
     * @param {number} relationshipId - Relationship ID
     * @param {string} status - New status ('active', 'pending', 'inactive')
     * @returns {Promise<Object>} API response
     */
    async updateRelationshipStatus(currentUser, currentEntity, relationshipId, status) {
        try {
            const response = await axios.put(`/api/entity/${currentEntity.entityId}/users/${currentUser.id}/relationships/${relationshipId}/status`, { status });
            return response.data;
        } catch (error) {
            console.error('Error updating relationship status:', error);
            throw this.handleError(error, this.t('errorUpdatingRelationshipStatus'));
        }
    }

    /**
     * Delete a relationship
     * Note: Entity access is validated server-side based on the relationship's entity and user's permissions
     * @param {number} relationshipId - Relationship ID
     * @returns {Promise<Object>} API response
     */
    async deleteRelationship(entityId, userId, relationshipId) {
        try {
            const response = await axios.delete(`/api/entity/${entityId}/users/${userId}/relationships/${relationshipId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting relationship:', error);
            throw this.handleError(error, this.t('errorDeletingRelationship'));
        }
    }

    /**
     * Get entity relationships (admin only)
     * Allows admins to view all relationships within their entity for oversight purposes
     * @param {number} entityId - Entity ID
     * @param {string} relationType - Optional filter by relation type
     * @param {string} status - Optional filter by status (default: 'active')
     * @returns {Promise<Object>} API response with entity relationships
     */
    async getEntityRelationships(entityId) {
        try {
            const response = await axios.get(`/api/entity/${entityId}/relationships`);
            return response.data;
        } catch (error) {
            console.error('Error fetching entity relationships:', error);
            throw this.handleError(error, this.t('errorFetchingEntityRelationships'));
        }
    }

    /**
     * Get all active users in entity (for admin dropdowns)
     * @param {number} entityId - Entity ID
     * @returns {Promise<Object>} API response with users array
     */
    async getEntityUsers(entityId) {
        try {
            const response = await axios.get(`/api/entity/${entityId}/users`);
            return response.data;
        } catch (error) {
            console.error('Error fetching entity users:', error);
            throw this.handleError(error, this.t('errorFetchingEntityUsers'));
        }
    }

    /**
     * Look up user by username or email (for user relationship requests)
     * @param {number} entityId - Entity ID to search within
     * @param {string} query - Username or email to search for
     * @returns {Promise<Object>} API response with user data
     */
    async lookupUser(entityId, query) {
        try {
            const response = await axios.get(`/api/entity/${entityId}/users/lookup/${encodeURIComponent(query)}`);
            return response.data;
        } catch (error) {
            console.error('Error looking up user:', error);
            throw this.handleError(error, this.t('errorLookingUpUser'));
        }
    }

    /**
     * Create relationship request (user-initiated, creates pending status)
     * @param {number} entityId - Entity ID
     * @param {Object} requestData - Request data
     * @returns {Promise<Object>} API response with created relationship request
     */
    async createRelationshipRequest(entityId, requestData) {
        try {
            const response = await axios.post(`/api/entity/${entityId}/relationships/request`, requestData);
            return response.data;
        } catch (error) {
            console.error('Error creating relationship request:', error);
            throw this.handleError(error, this.t('errorCreatingRelationshipRequest'));
        }
    }

    /**
     * Approve or reject relationship request
     * @param {number} entityId - Entity ID
     * @param {number} relationshipId - Relationship ID
     * @param {string} action - 'approve' or 'reject'
     * @returns {Promise<Object>} API response
     */
    async approveRelationshipRequest(entityId, relationshipId, action) {
        try {
            const response = await axios.put(`/api/entity/${entityId}/relationships/${relationshipId}/approve`, { action });
            return response.data;
        } catch (error) {
            console.error('Error processing relationship request:', error);
            throw this.handleError(error, this.t('errorProcessingRelationshipRequest'));
        }
    }

    /**
     * Search for users to create relationships with (legacy compatibility)
     * This uses the existing user search functionality
     * @param {number} entityId - Entity ID to search within
     * @param {string} searchTerm - Search term (name, email, username)
     * @returns {Promise<Object>} API response with user search results
     */
    async searchUsersForRelationship(entityId, searchTerm) {
        try {
            const response = await axios.get(`/api/settings/entity/${entityId}/users`, {
                params: {
                    search: searchTerm,
                    includeInactive: false // Only show active users
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error searching users for relationship:', error);
            throw this.handleError(error, this.t('errorSearchingUsers'));
        }
    }

    /**
     * Get available relation types with display names
     * @returns {Array} Array of relation type objects
     */
    getRelationTypes() {
        return [
            { value: 'guardian', label: this.t('relationTypeGuardian'), category: 'family' },
            { value: 'parent', label: this.t('relationTypeParent'), category: 'family' },
            { value: 'grandparent', label: this.t('relationTypeGrandparent'), category: 'family' },
            { value: 'sibling', label: this.t('relationTypeSibling'), category: 'family' },
            { value: 'spouse', label: this.t('relationTypeSpouse'), category: 'family' },
            { value: 'friend', label: this.t('relationTypeFriend'), category: 'social' },
            { value: 'colleague', label: this.t('relationTypeColleague'), category: 'professional' },
            { value: 'emergency_contact', label: this.t('relationTypeEmergencyContact'), category: 'emergency' },
            { value: 'other', label: this.t('relationTypeOther'), category: 'other' }
        ];
    }

    /**
     * Get available relationship statuses
     * @returns {Array} Array of status objects
     */
    getRelationStatuses() {
        return [
            { value: 'active', label: this.t('relationStatusActive'), color: 'green' },
            { value: 'pending', label: this.t('relationStatusPending'), color: 'yellow' },
            { value: 'inactive', label: this.t('relationStatusInactive'), color: 'gray' }
        ];
    }

    /**
     * Handle API errors and provide user-friendly messages
     * @param {Error} error - The error object
     * @param {string} defaultMessage - Default error message
     * @returns {Error} Formatted error
     */
    handleError(error, defaultMessage) {
        if (error.response) {
            // Server responded with error status
            const serverMessage = error.response.data?.error || error.response.data?.message;
            const userMessage = serverMessage || defaultMessage;
            
            const errorObj = new Error(userMessage);
            errorObj.status = error.response.status;
            errorObj.data = error.response.data;
            return errorObj;
        } else if (error.request) {
            // Network error
            const errorObj = new Error(this.t('networkError'));
            errorObj.network = true;
            return errorObj;
        } else {
            // Other error
            return new Error(defaultMessage);
        }
    }

    /**
     * Format relationship for display
     * @param {Object} relationship - Raw relationship object from API
     * @returns {Object} Formatted relationship for UI display
     */
    formatRelationshipForDisplay(relationship, currentUserId) {
        const relationTypes = this.getRelationTypes();
        const relationStatuses = this.getRelationStatuses();
        
        const relationType = relationTypes.find(rt => rt.value === relationship.relationType);
        const status = relationStatuses.find(rs => rs.value === relationship.status);
        
        return {
            ...relationship,
            relationTypeLabel: relationType?.label || relationship.relationType,
            relationTypeCategory: relationType?.category || 'other',
            statusLabel: status?.label || relationship.status,
            statusColor: status?.color || 'gray',
            relatedUserFullName: relationship.relatedUserId === currentUserId ? this.t("me") : `${relationship.relatedUserFirstName} ${relationship.relatedUserLastName}`.trim(),
            userFullName: relationship.userId === currentUserId ? this.t("me") : `${relationship.userFirstName} ${relationship.userLastName}`.trim(),
            createdByFullName: relationship.createdByFirstName && relationship.createdByLastName 
                ? `${relationship.createdByFirstName} ${relationship.createdByLastName}`.trim()
                : null,
            // Entity information for display
            entityName: relationship.entityName || this.t('unknownEntity')
        };
    }

    /**
     * Validate relationship data before sending to API
     * @param {Object} relationshipData - Relationship data to validate
     * @param {number} entityId - Entity ID for validation
     * @returns {Object} Validation result with isValid boolean and errors array
     */
    validateRelationshipData(relationshipData, entityId = null) {
        const errors = [];
        const relationTypes = this.getRelationTypes().map(rt => rt.value);

        if (entityId !== null && !entityId) {
            errors.push(this.t('entityIdRequired'));
        }

        if (!relationshipData.relatedUserId) {
            errors.push(this.t('relatedUserRequired'));
        }

        if (!relationshipData.relationType) {
            errors.push(this.t('relationshipTypeRequired'));
        } else if (!relationTypes.includes(relationshipData.relationType)) {
            errors.push(this.t('invalidRelationshipType'));
        }

        if (relationshipData.userId === relationshipData.relatedUserId) {
            errors.push(this.t('cannotRelateToSelf'));
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

// Export singleton instance
export default new RelationshipService();